// @file Surface share links store
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import PromiseQueue from '@@/bits/promise_queue'
import { getSectionBreakoutPostUrl } from '@@/bits/surface_share_links_helper'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { WallSectionBreakoutLink, WallSubmissionRequestProps } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { GroupByTypes, saveWall } from '@padlet/arvo'
import { ALERT_ICON } from 'bits/confirmation_dialog'
import { OzConfirmationDialogBoxButtonScheme } from 'library/v4/components/OzConfirmationDialogBox.vue'
import { keyBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'
import { useCopyToClipboard } from 'vuecomposables/copy_to_clipboard'

export const useSurfaceShareLinksStore = defineStore('surfaceShareLinks', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { genericFetchError } = useGlobalSnackbarStore()
  const queue = new PromiseQueue()

  /* Section Breakout */
  // State
  const isEnablingSectionBreakout = ref(false)
  const isSectionBreakoutEnabled = ref<boolean>(false)
  const sectionBreakoutLinks = ref<Record<number, WallSectionBreakoutLink> | null>(null)
  const disableSectionBreakoutTimer = ref<ReturnType<typeof setTimeout> | undefined>()
  const disableSectionBreakoutSnackbarUid = ref<string | null>(null)

  // Getters
  const sectionBreakoutIdentifier = computed<string | null>(() => surfaceVuexStore?.getters.sectionBreakoutIdentifier)
  const xSectionBreakoutLink = computed<boolean>(() => surfaceVuexStore?.getters?.canIAdminister ?? false)

  // Actions

  async function enableGroupBySections(): Promise<void> {
    try {
      await saveWall({
        ...surfaceVuexStore?.state.wall,
        wish_arrangement: {
          group_by: GroupByTypes.Section,
          is_sectionable: true,
        },
      })
    } catch (e) {
      genericFetchError()
      captureFetchException(e)
    }
  }

  function setSectionBreakoutLinksState(links: WallSectionBreakoutLink[] | null): void {
    if (links !== null) {
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } else {
      isSectionBreakoutEnabled.value = false
      sectionBreakoutLinks.value = null
    }
  }

  async function fetchSectionBreakoutLinks(): Promise<void> {
    if (!isAppUsing('sectionBreakout') || surfaceStore.isSectionBreakout || !xSectionBreakoutLink.value) {
      // early exit if not supposed to show section breakout links
      return
    }
    try {
      const links = await PadletApi.SectionBreakout.list(surfaceStore.wallId)
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } catch (e) {
      if (e.status === HttpCode.NotFound) {
        return
      }
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelListSectionBreakoutLinks' })
    }
  }

  async function enableSectionBreakout(): Promise<void> {
    // Clear any existing callbacks to disable
    if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
    globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')

    const links = await PadletApi.SectionBreakout.enable(surfaceStore.wallId)
    setSectionBreakoutLinksState(links)
  }

  async function disableSectionBreakout(): Promise<void> {
    try {
      await PadletApi.SectionBreakout.disable(surfaceStore.wallId)
      setSectionBreakoutLinksState(null)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelDisableSectionBreakout' })
    }
  }

  async function disableSectionBreakoutWithUndo(): Promise<void> {
    const SNACKBAR_DURATION = 3000

    disableSectionBreakoutSnackbarUid.value = globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Section breakout links closed'),
      actionText: 'Undo',
      persist: true,
      actionTextActions: [
        () => {
          // revert and close snackbar
          isSectionBreakoutEnabled.value = true
          if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
          globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
        },
      ],
    })

    // Hide section breakout first to give illusion of deletion
    isSectionBreakoutEnabled.value = false

    // Timeout to close snackbar and disable section breakout links
    disableSectionBreakoutTimer.value = setTimeout(() => {
      globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
      void disableSectionBreakout()
    }, SNACKBAR_DURATION)
  }

  function askToDisableSectionBreakout(): void {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...ALERT_ICON,
        title: __('Turn off section breakout links?'),
        body: __('Current links will no longer work. Turning links back on will create new links.'),
        confirmButtonText: __('Turn off'),
        cancelButtonText: __('Nevermind'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        forceFullWidthButtons: true,
        afterConfirmActions: [disableSectionBreakoutWithUndo],
      },
      { root: true },
    )
  }

  async function copySectionBreakoutLinkAndAlert(sectionId: number): Promise<void> {
    if (sectionBreakoutLinks.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: sectionBreakoutLinks.value[sectionId].link,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  async function copySectionBreakoutPostLinkAndAlert(postId: number): Promise<void> {
    if (sectionBreakoutIdentifier.value !== null) {
      const sectionBreakoutPostUrl = getSectionBreakoutPostUrl(
        surfaceStore.namespace,
        sectionBreakoutIdentifier.value,
        postId,
      )
      await useCopyToClipboard().copyToClipboard({
        text: sectionBreakoutPostUrl,
        globalSnackbarOptions: {
          message: __('Link copied to clipboard'),
          notificationType: SnackbarNotificationType.success,
        },
      })
    }
  }

  async function toggleSectionBreakout(): Promise<void> {
    const originalValue = isSectionBreakoutEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSectionBreakout.value) return

      if (isSectionBreakoutEnabled.value) {
        askToDisableSectionBreakout()
      } else {
        // Only show loading state if toggling to True
        isEnablingSectionBreakout.value = true
        await enableSectionBreakout()
        isEnablingSectionBreakout.value = false
      }
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSectionBreakout' })
      isSectionBreakoutEnabled.value = originalValue
    }
  }

  function showSectionInputDialog(): void {
    const payload = {
      title: __('New section'),
      shouldFadeIn: true,
      inputPlaceholder: 'Section title',
      inputAriaLabel: __('Section title'),
      submitButtonText: 'Create',
      submitActions: [
        async ({ inputValue }) => {
          await surfaceSectionsStore.createSectionWithName(inputValue.trim())
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          void surfaceVuexStore?.dispatch('inputDialog/setSubmitButtonDisable', {
            disable: inputValue.trim().length === 0,
          })
        },
      ],
    }
    void surfaceVuexStore?.dispatch('inputDialog/openInputDialog', payload, { root: true })
  }

  /* Submission Request */
  // State
  const isEnablingSubmissionRequest = ref(false)
  const isSubmissionRequestEnabled = ref(false)
  const submissionRequestLink = ref<string | null>(null)
  const submissionRequestRequiresLogin = ref<boolean | null>(null)
  const submissionRequestRedirectLocation = ref<string | null>(null)

  // Getters
  const xSubmissionRequestLink = computed<boolean>(() => surfaceVuexStore?.getters?.canIAdminister ?? false)

  // Actions
  async function enableSubmissionRequest(): Promise<void> {
    const linkAttributes = await PadletApi.SubmissionRequest.enable(surfaceStore.wallId)
    submissionRequestLink.value = linkAttributes.link
    submissionRequestRequiresLogin.value = linkAttributes.requires_login
    submissionRequestRedirectLocation.value = linkAttributes.redirect_location
    isSubmissionRequestEnabled.value = true
  }

  async function disableSubmissionRequest(): Promise<void> {
    try {
      await PadletApi.SubmissionRequest.disable(surfaceStore.wallId)
      isSubmissionRequestEnabled.value = false
      submissionRequestLink.value = null
      submissionRequestRequiresLogin.value = null
      submissionRequestRedirectLocation.value = null
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelDisableSubmissionRequest' })
    }
  }

  function askToDisableSubmissionRequest(): void {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...ALERT_ICON,
        title: __('Turn off submission request link?'),
        body: __('Current link will no longer work. Turning link back on will create a new link.'),
        confirmButtonText: __('Turn off'),
        cancelButtonText: __('Nevermind'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        forceFullWidthButtons: true,
        afterConfirmActions: [disableSubmissionRequest],
      },
      { root: true },
    )
  }

  async function toggleSubmissionRequest(): Promise<void> {
    const originalValue = isSubmissionRequestEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSubmissionRequest.value) return

      if (isSubmissionRequestEnabled.value) {
        await askToDisableSubmissionRequest()
      } else {
        // Only show loading state if toggling to True
        isEnablingSubmissionRequest.value = true
        await enableSubmissionRequest()
        isEnablingSubmissionRequest.value = false
      }
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSubmissionRequest' })
      isSubmissionRequestEnabled.value = originalValue
    }
  }

  function getSubmissionRequestProps(
    payload: { requiresLogin?: boolean; redirectLocation?: string } = {},
  ): WallSubmissionRequestProps | null {
    const requiresLogin =
      payload.requiresLogin !== undefined ? payload.requiresLogin : submissionRequestRequiresLogin.value
    const redirectLocation =
      payload.redirectLocation !== undefined ? payload.redirectLocation : submissionRequestRedirectLocation.value

    if (requiresLogin == null || redirectLocation == null) return null

    return {
      requires_login: requiresLogin,
      redirect_location: redirectLocation,
    }
  }

  async function updateSubmissionRequestProps(
    oldProps: WallSubmissionRequestProps,
    newProps: WallSubmissionRequestProps,
  ): Promise<void> {
    try {
      const linkAttributes = await PadletApi.SubmissionRequest.update(surfaceStore.wallId, {
        requires_login: newProps.requires_login,
        redirect_location: newProps.redirect_location,
      })
      submissionRequestLink.value = linkAttributes.link
      submissionRequestRequiresLogin.value = linkAttributes.requires_login
      submissionRequestRedirectLocation.value = linkAttributes.redirect_location
      isSubmissionRequestEnabled.value = true
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelUpdateSubmissionRequestProps' })
      submissionRequestRedirectLocation.value = oldProps.redirect_location
      submissionRequestRequiresLogin.value = oldProps.requires_login
    }
  }

  async function enqueueToggleSubmissionRequestRequiresLogin(): Promise<void> {
    if (submissionRequestRequiresLogin.value === null) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ requiresLogin: !submissionRequestRequiresLogin.value })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRequiresLogin.value = !submissionRequestRequiresLogin.value

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function enqueueUpdateSubmissionRequestRedirectLocation(redirectLocation: string): Promise<void> {
    if (submissionRequestRedirectLocation.value === null) return
    if (submissionRequestRedirectLocation.value === redirectLocation) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ redirectLocation })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRedirectLocation.value = redirectLocation

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }
  return {
    /* Section Breakout */
    // State
    isEnablingSectionBreakout,
    isSectionBreakoutEnabled,
    sectionBreakoutLinks,
    sectionBreakoutIdentifier,

    // Getters
    xSectionBreakoutLink,

    // Actions
    enableGroupBySections,
    toggleSectionBreakout,
    fetchSectionBreakoutLinks,
    copySectionBreakoutLinkAndAlert,
    showSectionInputDialog,
    setSectionBreakoutLinksState,
    copySectionBreakoutPostLinkAndAlert,

    /* Submission Request */
    // State
    isEnablingSubmissionRequest,
    isSubmissionRequestEnabled,
    submissionRequestLink,
    submissionRequestRequiresLogin,
    submissionRequestRedirectLocation,

    // Getters

    // Getters
    xSubmissionRequestLink,

    // Actions
    toggleSubmissionRequest,
    enqueueToggleSubmissionRequestRequiresLogin,
    enqueueUpdateSubmissionRequestRedirectLocation,
  }
})
